import { getLocalizedRawAmount } from 'apps/promo/helpers/formats';

type Tariff = {
    minAmount?: number;
    maxAmount?: number;
    percent: number;
    fix: number;
};

type TariffList = Tariff[];

type TariffValueResult = {
    value: number;
};

type MinTariffValueResult = {
    value: number;
    canBeZero: boolean;
    minTariffList: TariffList | undefined;
};

//isDebit = from
const getTariffValueForAmount = (tariffList: TariffList, amount: number, isDebit: boolean): TariffValueResult => {
    const tariff = tariffList.find((tariff) => {
        return (!tariff.minAmount || amount >= tariff.minAmount) && (!tariff.maxAmount || amount <= tariff.maxAmount);
    });

    const debitTariff = tariff ? amount * (tariff.percent / 100) + tariff.fix : 0;
    const creditTariff = tariff ? (amount + tariff.fix) / (1 - tariff.percent / 100) - amount : 0;

    return {
        value: isDebit ? debitTariff : creditTariff,
    };
};

const getMinTariffValueForAmount = (tariffLists: TariffList[], amount: number, isDebit: boolean): MinTariffValueResult => {
    let minTariffValue: number | undefined;
    let minTariffList: TariffList | undefined;
    let canBeZero = false;

    for (const tariffList of tariffLists) {
        const tariffValue = getTariffValueForAmount(tariffList, amount, isDebit);

        if (tariffValue.value === 0) {
            canBeZero = true;
            continue;
        }

        if (minTariffValue === undefined || tariffValue.value < minTariffValue) {
            minTariffValue = tariffValue.value;
            minTariffList = tariffList;
        }
    }

    return { value: minTariffValue ?? 0, canBeZero, minTariffList };
};

export const getTotalTariffValueForAmount = (amount: number, fromTariffs: TariffList[], toTariffs: TariffList[], isDebit: boolean): object => {
    const minTariffFrom = getMinTariffValueForAmount(fromTariffs, amount, isDebit);
    const minTariffTo = getMinTariffValueForAmount(toTariffs, amount, isDebit);

    if (minTariffFrom.canBeZero && minTariffTo.canBeZero) {
        return {
            commissionAmount: Math.min(minTariffFrom.value, minTariffTo.value) || Math.max(minTariffFrom.value, minTariffTo.value),
            appliedTariffFrom: minTariffFrom.minTariffList,
            appliedTariffTo: minTariffTo.minTariffList,
        };
    }

    if (minTariffFrom.canBeZero) {
        return { commissionAmount: minTariffTo.value, appliedTariffFrom: null, appliedTariffTo: minTariffTo.minTariffList };
    }

    if (minTariffTo.canBeZero) {
        return { commissionAmount: minTariffFrom.value, appliedTariffFrom: minTariffFrom.minTariffList, appliedTariffTo: null };
    }

    return {
        commissionAmount: minTariffFrom.value + minTariffTo.value,
        commissionFrom: { amount: minTariffFrom.value, tariffList: minTariffFrom.minTariffList },
        commissionTo: { amount: minTariffTo.value, tariffList: minTariffTo.minTariffList },
    };
};

export const formatTariffIntervals = (
    appliedTariff: Tariff[],
    tariffIntervalPercent: string,
    tariffIntervalPercentFirst: string,
    tariffIntervalPercentLast: string,
    tariffIntervalFix: string,
    tariffIntervalFixFirst: string,
    tariffIntervalFixLast: string,
    commissionCurrency: string,
    language: string,
    country: string,
): string[] => {
    if (!appliedTariff) return [];
    return appliedTariff.map((tariff, index) => {
        const { percent, fix, minAmount, maxAmount } = tariff;

        const fee = percent || fix;

        const isFirst = index === 0;
        const isLast = index === appliedTariff.length - 1;

        const hasRange = minAmount != null || maxAmount != null;

        const fmt = (val: number) => getLocalizedRawAmount(language, country, val) ?? String(val);

        if (!hasRange) {
            return percent ? `${fmt(fee ?? 0)}%` : `${fmt(fee ?? 0)} ${commissionCurrency}`;
        }

        const template =
            (percent
                ? isFirst
                    ? tariffIntervalPercentFirst
                    : isLast
                      ? tariffIntervalPercentLast
                      : tariffIntervalPercent
                : isFirst
                  ? tariffIntervalFixFirst
                  : isLast
                    ? tariffIntervalFixLast
                    : tariffIntervalFix) ?? '';

        return template
            .replaceAll('{fee}', fmt(fee ?? 0))
            .replaceAll('{currency}', commissionCurrency)
            .replace('{minAmount}', fmt(minAmount ?? 0))
            .replace('{maxAmount}', fmt(maxAmount ?? 0));
    });
};
